import { cn } from '@/lib/utils'

interface Tab {
  /** 탭 고유 키 */
  key: string
  /** 탭 라벨 */
  label: string
}

interface TabGroupProps {
  /** 탭 목록 */
  tabs: Tab[]
  /** 현재 활성 탭 키 */
  activeTab: string
  /** 탭 변경 콜백 */
  onTabChange: (key: string) => void
  /** 추가 CSS 클래스 */
  className?: string
}

/**
 * 탭 그룹 컴포넌트 v2.0
 *
 * - 필 스타일 배경 + 라운드 디자인
 * - 활성 탭: 흰색 배경 + 그림자 (세그먼트 컨트롤 스타일)
 * - 접근성: role="tablist", role="tab", aria-selected
 * - 큰 터치 영역, 명확한 활성 상태
 */
export default function TabGroup({
  tabs,
  activeTab,
  onTabChange,
  className,
}: TabGroupProps) {
  return (
    <div
      role="tablist"
      aria-label="탭 메뉴"
      className={cn(
        'flex gap-1 rounded-2xl bg-cb-gray-100 p-1',
        className
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === activeTab
        return (
          <button
            key={tab.key}
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.key}`}
            id={`tab-${tab.key}`}
            onClick={() => onTabChange(tab.key)}
            className={cn(
              'flex-1 py-2.5 text-center text-body-sm font-semibold rounded-xl transition-all duration-200',
              'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cb-primary',
              isActive
                ? 'bg-white text-cb-primary shadow-sm'
                : 'text-cb-gray-500 hover:text-cb-gray-700'
            )}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

interface TabPanelProps {
  /** 탭 키 (TabGroup의 tab.key와 일치) */
  tabKey: string
  /** 현재 활성 탭 키 */
  activeTab: string
  /** 탭 패널 내용 */
  children: React.ReactNode
  /** 추가 CSS 클래스 */
  className?: string
}

/**
 * 탭 패널 컴포넌트.
 * TabGroup과 함께 사용합니다.
 */
export function TabPanel({ tabKey, activeTab, children, className }: TabPanelProps) {
  if (tabKey !== activeTab) return null

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${tabKey}`}
      aria-labelledby={`tab-${tabKey}`}
      className={cn('py-2', className)}
    >
      {children}
    </div>
  )
}
